/*
  自動スライダー（商品画像・KV共通）
  ------------------------------------------------------------------
  カスタム要素（Web Components）として実装しています。
  Shopifyのテーマエディタはセクション編集のたびにHTMLを差し替えますが、
  その際 <script> タグは再実行されません。カスタム要素であればDOMに
  挿入された時点で connectedCallback が自動的に呼ばれるため、
  編集直後でもスライダーが動き続けます。

  ・スライド = 直下の .w26-slide（KV）、無ければ img（商品画像）
  ・表示時間 = 各スライドの data-duration（ミリ秒）、無ければ data-interval
  ・data-pause-on-hover があると、マウスを乗せている間・フォーカス中は止まる
  ・商品画像の表示順は「① 商品物撮り画像 → ② イメージ画像」です。
*/

class LpWinter2026Slider extends HTMLElement {
  connectedCallback() {
    const own = Array.from(this.children).filter((el) => el.classList.contains("w26-slide"));
    this.slides = own.length > 0 ? own : Array.from(this.querySelectorAll("img"));
    if (this.slides.length === 0) return;

    // 1枚目が未指定なら先頭を表示状態にする
    if (!this.slides.some((slide) => slide.classList.contains("active"))) {
      this.slides[0].classList.add("active");
    }

    if (this.slides.length < 2) return;

    this.index = this.slides.findIndex((slide) => slide.classList.contains("active"));
    if (this.index < 0) this.index = 0;

    this.bindControls();

    if (this.hasAttribute("data-pause-on-hover")) {
      this.pause = () => this.stop();
      this.resume = () => {
        if (!this.matches(":hover") && !this.contains(document.activeElement)) this.schedule();
      };
      this.addEventListener("mouseenter", this.pause);
      this.addEventListener("mouseleave", this.resume);
      this.addEventListener("focusin", this.pause);
      this.addEventListener("focusout", () => setTimeout(this.resume, 0));
    }

    this.schedule();
  }

  disconnectedCallback() {
    this.stop();
  }

  duration() {
    const own = parseInt(this.slides[this.index].dataset.duration, 10);
    return own || parseInt(this.dataset.interval, 10) || 3500;
  }

  schedule() {
    this.stop();
    this.timer = setTimeout(() => {
      this.next();
      this.schedule();
    }, this.duration());
  }

  stop() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  next() {
    this.goTo(this.index + 1);
  }

  goTo(target) {
    const count = this.slides.length;
    const index = ((target % count) + count) % count;
    this.slides[this.index].classList.remove("active");
    this.index = index;
    this.slides[this.index].classList.add("active");

    if (this.dots) {
      this.dots.forEach((dot, i) => {
        const current = i === this.index;
        dot.classList.toggle("is-active", current);
        if (current) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });
    }
  }

  /* 手動で切り替えたときは、そのスライドの表示時間を最初から数え直す */
  manual(target) {
    this.goTo(target);
    if (!this.matches(":hover") || !this.hasAttribute("data-pause-on-hover")) this.schedule();
  }

  /*
    KV用の操作部（矢印・ドット・スワイプ）。
    操作部は同じ .w26-kv の中にあるときだけ接続する（商品画像のスライダーには無い）。
  */
  bindControls() {
    const root = this.closest(".w26-kv");
    if (!root) return;

    const prev = root.querySelector("[data-w26-kv-prev]");
    const next = root.querySelector("[data-w26-kv-next]");
    if (prev) prev.addEventListener("click", () => this.manual(this.index - 1));
    if (next) next.addEventListener("click", () => this.manual(this.index + 1));

    this.dots = Array.from(root.querySelectorAll("[data-w26-kv-dot]"));
    this.dots.forEach((dot) => {
      dot.addEventListener("click", () => this.manual(parseInt(dot.dataset.w26KvDot, 10) || 0));
    });

    // 左右スワイプ（縦スクロールは邪魔しない）。スワイプ直後のタップ判定でリンクへ飛ばないようにする
    let startX = 0;
    let startY = 0;
    let swiped = false;
    this.addEventListener("touchstart", (event) => {
      const touch = event.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      swiped = false;
    }, { passive: true });
    this.addEventListener("touchend", (event) => {
      const touch = event.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        swiped = true;
        this.manual(this.index + (dx < 0 ? 1 : -1));
      }
    }, { passive: true });
    this.addEventListener("click", (event) => {
      if (swiped) {
        event.preventDefault();
        event.stopPropagation();
        swiped = false;
      }
    }, true);

    root.classList.add("is-ready");
  }
}

if (!customElements.get("lp-winter2026-slider")) {
  customElements.define("lp-winter2026-slider", LpWinter2026Slider);
}

/*
  KVから商品カードへのページ内リンクをなめらかにスクロールさせる
  （LP内の "#..." リンクだけが対象。視差効果を減らす設定では瞬時に移動）
*/
document.addEventListener("click", (event) => {
  const link = event.target.closest(".lp-winter2026 a[href^='#']");
  if (!link) return;
  const target = document.getElementById(decodeURIComponent(link.getAttribute("href").slice(1)));
  if (!target) return;
  event.preventDefault();
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  history.replaceState(null, "", link.getAttribute("href"));
});
